'use client';

import { useEffect, useState, useMemo } from 'react';
import {
  DocumentExceptionErrorStatusEnum,
  DocumentExceptionErrorTypeEnum,
} from '../../common/enums.ts';
import queryHooks from '../../common/queryHooks/index.ts';
import { useDebounceValue } from '../../common/useDebounce.ts';
import { useNavigate } from 'react-router-dom';
import M from 'materialize-css';
import styled from '@emotion/styled';

export default function DocumentExceptions() {
  const navigate = useNavigate();

  const errorStatusOptions = useMemo(
    () => [
      { label: 'All', value: null },
      { label: 'Open', value: DocumentExceptionErrorStatusEnum.Open },
      { label: 'Closed', value: DocumentExceptionErrorStatusEnum.Closed },
    ],
    []
  );

  const errorTypeOptions = useMemo(
    () => [
      { label: 'All', value: null },
      {
        label: 'MalformedEpcisXmlError',
        value: DocumentExceptionErrorTypeEnum.MalformedEpcisXmlError,
      },
      {
        label: 'StandardBusinessDocumentHeaderError',
        value:
          DocumentExceptionErrorTypeEnum.StandardBusinessDocumentHeaderError,
      },
      {
        label: 'HeaderHeaderVersionError',
        value: DocumentExceptionErrorTypeEnum.HeaderHeaderVersionError,
      },
      {
        label: 'HeaderReceiverSglnError',
        value: DocumentExceptionErrorTypeEnum.HeaderReceiverSglnError,
      },
      {
        label: 'HeaderSenderError',
        value: DocumentExceptionErrorTypeEnum.HeaderSenderError,
      },
      {
        label: 'HeaderDocumentIdentificationError',
        value: DocumentExceptionErrorTypeEnum.HeaderDocumentIdentificationError,
      },
      {
        label: 'HeaderDocumentIdentificationTypeError',
        value:
          DocumentExceptionErrorTypeEnum.HeaderDocumentIdentificationTypeError,
      },
      {
        label: 'HeaderDocumentIdentificationCreationDateAndTimeError',
        value:
          DocumentExceptionErrorTypeEnum.HeaderDocumentIdentificationCreationDateAndTimeError,
      },
      {
        label: 'HeaderDocumentIdentificationInstanceIdentifierError',
        value:
          DocumentExceptionErrorTypeEnum.HeaderDocumentIdentificationInstanceIdentifierError,
      },
      {
        label: 'LocationVocabularyError',
        value: DocumentExceptionErrorTypeEnum.LocationVocabularyError,
      },
      {
        label: 'MedVocabularyError',
        value: DocumentExceptionErrorTypeEnum.MedVocabularyError,
      },
      {
        label: 'EventMedsError',
        value: DocumentExceptionErrorTypeEnum.EventMedsError,
      },
      {
        label: 'EventListError',
        value: DocumentExceptionErrorTypeEnum.EventListError,
      },
      {
        label: 'EventListEventTimeError',
        value: DocumentExceptionErrorTypeEnum.EventListEventTimeError,
      },
      {
        label: 'EventListEventTimeZoneOffsetError',
        value: DocumentExceptionErrorTypeEnum.EventListEventTimeZoneOffsetError,
      },
      {
        label: 'EventListActionError',
        value: DocumentExceptionErrorTypeEnum.EventListActionError,
      },
      {
        label: 'EventListBizStepError',
        value: DocumentExceptionErrorTypeEnum.EventListBizStepError,
      },
      {
        label: 'EventListDispositionError',
        value: DocumentExceptionErrorTypeEnum.EventListDispositionError,
      },
      {
        label: 'EventListEpcListError',
        value: DocumentExceptionErrorTypeEnum.EventListEpcListError,
      },
      {
        label: 'EventListReadPointError',
        value: DocumentExceptionErrorTypeEnum.EventListReadPointError,
      },
      {
        label: 'EventListBizTransactionListError',
        value: DocumentExceptionErrorTypeEnum.EventListBizTransactionListError,
      },
      {
        label: 'EventListExtensionError',
        value: DocumentExceptionErrorTypeEnum.EventListExtensionError,
      },
      {
        label: 'EventListSourceListError',
        value: DocumentExceptionErrorTypeEnum.EventListSourceListError,
      },
      {
        label: 'EventListDestinationError',
        value: DocumentExceptionErrorTypeEnum.EventListDestinationError,
      },
      {
        label: 'EventListParentIdError',
        value: DocumentExceptionErrorTypeEnum.EventListParentIdError,
      },
      {
        label: 'AggregationEventOneOfError',
        value: DocumentExceptionErrorTypeEnum.AggregationEventOneOfError,
      },
      {
        label: 'TransactionEventOneOfError',
        value: DocumentExceptionErrorTypeEnum.TransactionEventOneOfError,
      },
      {
        label: 'ObjectEventOneOfError',
        value: DocumentExceptionErrorTypeEnum.ObjectEventOneOfError,
      },
      {
        label: 'UnknownLocationError',
        value: DocumentExceptionErrorTypeEnum.UnknownLocationError,
      },
      {
        label: 'UnknownMedError',
        value: DocumentExceptionErrorTypeEnum.UnknownMedError,
      },
      {
        label: 'InvalidEventReadPointError',
        value: DocumentExceptionErrorTypeEnum.InvalidEventReadPointError,
      },
      {
        label: 'UnknownAddEpcError',
        value: DocumentExceptionErrorTypeEnum.UnknownAddEpcError,
      },
      {
        label: 'UnknownShippingEpcError',
        value: DocumentExceptionErrorTypeEnum.UnknownShippingEpcError,
      },
      {
        label: 'ShippedEpcAreParentOrDefinedError',
        value: DocumentExceptionErrorTypeEnum.ShippedEpcAreParentOrDefinedError,
      },
      {
        label: 'UnknownParentEpcError',
        value: DocumentExceptionErrorTypeEnum.UnknownParentEpcError,
      },
      {
        label: 'AllMedsNotShippedError',
        value: DocumentExceptionErrorTypeEnum.AllMedsNotShippedError,
      },
    ],
    []
  );

  const [filters, setFilters] = useState({
    sender: '',
    receiver: '',
    startDate: '',
    endDate: '',
    status: null,
    errorType: null,
  });
  const debouncedFilters = useDebounceValue(filters, 500);

  // Main data store
  const { data: documentExceptions = null, isLoading } =
    queryHooks.documentException.useSearch(debouncedFilters, {
      staleTime: 5 * 60 * 1000,
    });

  const TableRow = useMemo(
    () => styled.tr`
      cursor: pointer;
      &:hover {
        background-color: #e0f2fe;
      }
    `,
    []
  );

  const filterChange = ({ target: { id, value } }) => {
    setFilters(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  const openDetailsPage = id => {
    if (!id) return;
    navigate(`/exceptions/${id}`, false);
  };

  useEffect(() => {
    M.FormSelect.init(document.querySelectorAll('select'));
  }, []);

  return (
    <div className="">
      <div className="">
        <div className="row">
          <div className="input-field col s6 m6 l6">
            <input
              id="sender"
              type="text"
              value={filters.sender || ''}
              onChange={filterChange}
            />
            <label htmlFor="sender">Sender</label>
          </div>
          <div className="input-field col s6 m6 l6">
            <input
              id="receiver"
              type="text"
              value={filters.receiver || ''}
              onChange={filterChange}
            />
            <label htmlFor="receiver">Receiver</label>
          </div>
          <div className="input-field col s3 m3 l3">
            <input
              id="startDate"
              type="date"
              value={filters.startDate || ''}
              onChange={filterChange}
            />
            <label htmlFor="startDate">Start</label>
          </div>
          <div className="input-field col s3 m3 l3">
            <input
              id="endDate"
              type="date"
              value={filters.endDate || ''}
              onChange={filterChange}
            />
            <label htmlFor="endDate">End</label>
          </div>
          <div className="input-field col s3 m3 l3">
            <select
              type="select"
              id="status"
              onChange={filterChange}
              label="Error Status"
              value={filters.status}
              options={errorStatusOptions}
            >
              {errorStatusOptions.map(option => (
                <option value={option.value}>{option.label}</option>
              ))}
            </select>
            <label htmlFor="status">Error Status</label>
          </div>

          <div className="input-field col s3 m3 l3">
            <select
              type="select"
              id="errorType"
              onChange={filterChange}
              label="Error Type"
              value={filters.errorType}
              options={errorTypeOptions}
            >
              {errorTypeOptions.map(option => (
                <option value={option.value}>{option.label}</option>
              ))}
            </select>
            <label htmlFor="errorType">Error Type</label>
          </div>
        </div>
        {isLoading ? (
          <div
            className="h-64"
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <div className="preloader-wrapper big active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>{' '}
          </div>
        ) : (
          <div className="card" style={{ padding: '16px' }}>
            <table>
              <thead>
                <tr>
                  {[
                    'Ticket #',
                    'Sender',
                    'Receiver',
                    'Error',
                    'Status',
                    'Identifier',
                    'PO Number',
                    'Date Created',
                    '',
                  ].map(h => (
                    <th>{h}</th>
                  ))}
                </tr>
              </thead>
              {(documentExceptions ?? []).map((ee, index) => {
                return (
                  <tbody>
                    <TableRow
                      key={index}
                      onClick={() => openDetailsPage(ee.id)}
                    >
                      <td>{ee?.ticketNumber}</td>
                      <td>{ee?.sender}</td>
                      <td>{ee?.receiver}</td>
                      <td>{ee?.currentErrorType}</td>
                      <td
                        className={`${ee?.currentErrorStatus?.toLowerCase()}`}
                      >
                        {ee?.currentErrorStatus}
                      </td>
                      <td>{ee?.instanceIdentifier}</td>
                      <td>{ee?.poNumber}</td>
                      <td>{ee?.dateCreated}</td>
                    </TableRow>
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
